/**
 * Portada.
 *
 * El unico `<h1>` de la pagina es la propuesta de valor, no el nombre de la
 * tienda: es lo que Google muestra y lo que le dice a alguien que llega por
 * primera vez que esto es una ferreteria de carpinteria, no una ferreteria
 * generica. Va oculto (solo lectores): quienes somos, en pantalla, es la banda
 * de `PresentacionTienda`, a un tercio de los destacados. De ahi para abajo,
 * cada bloque es un `<section>` con su `<h2>`.
 *
 * Las tres filas de banners (carrusel, promociones y cuadros) van FUERA del
 * `.contenedor`, de lado a lado de la pantalla, y el resto de la portada sigue
 * dentro del ancho de siempre (rediseño del 14-09-2026, a pedido del cliente).
 * La cinta de marcas tambien va de lado a lado, al final, y la banda de quienes
 * somos (`SeccionDestacados`): por eso `.pagina` no es un `.contenedor` y cada
 * tramo pone el suyo.
 */
import { SITIO } from '../configuracion/sitio';
import { Cargando, AvisoError } from '../componentes/comunes/EstadoPeticion';
import { GrillaProductos } from '../componentes/catalogo/GrillaProductos';
import { Carrusel } from '../componentes/inicio/Carrusel';
import { CintaMarcas } from '../componentes/inicio/CintaMarcas';
import { FilaCuadros } from '../componentes/inicio/FilaCuadros';
import { FilaPromociones } from '../componentes/inicio/FilaPromociones';
import { GrillaCategorias } from '../componentes/inicio/GrillaCategorias';
import { PanelWhatsapp } from '../componentes/inicio/PanelWhatsapp';
import { SeccionDestacados } from '../componentes/inicio/SeccionDestacados';
import { SeccionInicio } from '../componentes/inicio/SeccionInicio';
import { TiraConfianza } from '../componentes/inicio/TiraConfianza';
import { usePeticion } from '../hooks/use-peticion';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaOrganizacion, esquemaSitio } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerInicio } from '../servicios/catalogo.servicio';
import { agruparBanners } from '../utilidades/banners';
import { numero } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';

export function Inicio() {
  const { estado, datos, error } = usePeticion((senal) => obtenerInicio(senal), []);
  const filas = agruparBanners(datos?.banners ?? []);

  return (
    <>
      <Seo
        titulo="Ferretería El Carpintero"
        descripcion={SITIO.descripcion}
        ruta={rutas.inicio()}
        imagen="/demo/banners/banner-5.webp"
      />
      <DatosEstructurados esquemas={[esquemaOrganizacion(), esquemaSitio()]} />

      <h1 className="solo-lectores">Todo para el carpintero, el mueblista y el tapicero</h1>

      {/* El aviso de carga va solo y fuera de `.pagina`: si quedara adentro, al
          llegar los datos los banners aparecerian encima y empujarian la pagina
          ya pintada ~730 px hacia abajo (CLS 0,65 en PageSpeed, 04-10-2026). */}
      {estado === 'cargando' ? (
        <div className="contenedor">
          <Cargando que="la portada" pagina />
        </div>
      ) : null}

      {datos !== null ? (
        <div className="portada-banners">
          <Carrusel banners={filas.carrusel} />
          <FilaPromociones banners={filas.promociones} />
          <FilaCuadros banners={filas.cuadros} />
        </div>
      ) : null}

      {estado !== 'cargando' ? (
        <div className="pagina">
          <div className="contenedor">
            {estado === 'error' ? <AvisoError error={error} /> : null}
            {datos !== null ? <TiraConfianza /> : null}
          </div>

          {datos !== null ? (
            <>
              <SeccionDestacados
                productos={datos.destacados}
                fotoTienda={datos.fotoTienda}
                bajada={`Selección transversal de nuestras ${numero(datos.resumen.categorias)} categorías`}
              />

              <div className="contenedor">
                <SeccionInicio
                  id="categorias"
                  titulo="Un mundo para el carpintero"
                  bajada={`${numero(datos.resumen.lineas)} líneas · ${numero(datos.resumen.categorias)} categorías`}
                >
                  <GrillaCategorias categorias={datos.categoriasDestacadas} />
                </SeccionInicio>

                {datos.ofertas.length > 0 ? (
                  <SeccionInicio id="ofertas" titulo="Ofertas vigentes">
                    <GrillaProductos productos={datos.ofertas} titulo="Productos en oferta" />
                  </SeccionInicio>
                ) : null}

                <PanelWhatsapp />
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {datos !== null ? <CintaMarcas marcas={datos.marcas} /> : null}
    </>
  );
}
